import type { LifecycleStateFilterDto } from './lifecycle-state.dto';
import { isArchivedLifecycle } from './lifecycle-state.dto';

export const RELATED_RECORDS_LIFECYCLE_STATE =
  'all' as const satisfies LifecycleStateFilterDto;

export const LIST_BADGE_LIFECYCLE_STATE =
  'all' as const satisfies LifecycleStateFilterDto;

export const DEFAULT_LIFECYCLE_STATE_FILTER =
  'all' as const satisfies LifecycleStateFilterDto;

export const resolveListLifecycleState = (
  lifecycleState?: LifecycleStateFilterDto | null,
): LifecycleStateFilterDto => lifecycleState ?? DEFAULT_LIFECYCLE_STATE_FILTER;

export const resolveLifecycleStateQuery = ({
  isEnabled,
  lifecycleState,
}: {
  isEnabled: boolean;
  lifecycleState: LifecycleStateFilterDto;
}): LifecycleStateFilterDto | undefined =>
  isEnabled ? lifecycleState : undefined;

export type ArchiveLifecycleSource = {
  isArchived?: boolean;
  lifecycle?: { state: string } | null;
};

export const isArchivedEntity = (
  entity: ArchiveLifecycleSource | null | undefined,
): boolean => {
  if (!entity) {
    return false;
  }

  return entity.isArchived === true || isArchivedLifecycle(entity.lifecycle);
};

export const excludeArchivedEntities = <T>(
  items: readonly T[],
  isArchived: (item: T) => boolean,
): T[] => items.filter((item) => !isArchived(item));

export type MutableArchiveSelection<T> =
  | { status: 'empty' }
  | { status: 'ready'; items: T[] };

export const takeMutableArchiveSelection = <T>(
  items: readonly T[],
  isArchived: (item: T) => boolean,
): MutableArchiveSelection<T> => {
  const mutable = excludeArchivedEntities(items, isArchived);

  return mutable.length === 0
    ? { status: 'empty' }
    : { status: 'ready', items: mutable };
};

export const areArchivedListMutationsDisabled = ({
  lifecycleQueryParam,
  hasLifecycleOverride,
}: {
  lifecycleQueryParam?: LifecycleStateFilterDto | null;
  hasLifecycleOverride?: boolean;
}): boolean =>
  lifecycleQueryParam === 'archived' || Boolean(hasLifecycleOverride);

export const withLifecycleState = <T extends object>(
  params: T,
  lifecycleState: LifecycleStateFilterDto | undefined,
): T & { lifecycleState: LifecycleStateFilterDto | undefined } => ({
  ...params,
  lifecycleState,
});

export const archivedEntityDisplayTitle = ({
  title,
  isEditMode,
  editLabel,
}: {
  title: string;
  isEditMode?: boolean;
  editLabel?: string;
}): string => (isEditMode && editLabel ? `${editLabel}: ${title}` : title);
